import { useTranslation } from "react-i18next";
import cn from "classnames";
import type { EquityChangesResponse } from "@/features/accounting/financial-reports/types";
import { buildEquityTableRows } from "@/features/accounting/financial-reports/utils";

interface Props {
  rows: EquityChangesResponse["rows"];
}

const HEAD_CELL_CLASS = "border surface-base-stroke px-3 py-2 text-body-bold-sm content-base-primary text-center align-middle";
const BODY_CELL_CLASS = "border surface-base-stroke px-3 py-2";

export default function EquityChangesTable({ rows }: Props) {
  const { t } = useTranslation("FinancialReportPage");
  const tableRows = buildEquityTableRows(rows);

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th rowSpan={2} className={cn(HEAD_CELL_CLASS, "min-w-[320px] text-left")}>
            {t("equityChanges.table.componentName")}
          </th>
          <th rowSpan={2} className={HEAD_CELL_CLASS}>
            {t("equityChanges.table.rowCode")}
          </th>
          <th colSpan={6} className={HEAD_CELL_CLASS}>
            {t("equityChanges.table.ownersGroup")}
          </th>
          <th rowSpan={2} className={HEAD_CELL_CLASS}>
            {t("equityChanges.table.columns.minorityInterest")}
          </th>
          <th rowSpan={2} className={HEAD_CELL_CLASS}>
            {t("equityChanges.table.columns.total")}
          </th>
        </tr>
        <tr>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.shareCapital")}</th>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.sharePremium")}</th>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.treasuryShares")}</th>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.ociComponents")}</th>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.retainedEarnings")}</th>
          <th className={HEAD_CELL_CLASS}>{t("equityChanges.table.columns.otherCapital")}</th>
        </tr>
        <tr>
          {Array.from({ length: 10 }).map(function renderGraphNumber(_, index) {
            return (
              <th
                key={index}
                className="border surface-base-stroke px-3 py-1 text-body-regular-xs content-base-low text-center surface-secondary-fill">
                {index + 1}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {tableRows.map(function renderTableRow(row) {
          return (
            <tr key={row.key}>
              <td
                className={cn(
                  BODY_CELL_CLASS,
                  row.indent === 1 && "pl-7",
                  row.indent === 2 && "pl-11",
                  row.bold ? "text-body-bold-sm content-base-primary" : "text-body-regular-sm content-base-primary",
                  row.italic && "italic content-base-low",
                )}>
                {t(row.labelKey)}
              </td>
              <td
                className={cn(
                  BODY_CELL_CLASS,
                  "text-center",
                  row.bold ? "text-body-bold-sm content-base-primary" : "text-body-regular-sm content-base-secondary",
                )}>
                {row.code}
              </td>
              {row.cells.map(function renderValueCell(cell, cellIndex) {
                return (
                  <td
                    key={cellIndex}
                    className={cn(
                      BODY_CELL_CLASS,
                      "text-right whitespace-nowrap",
                      row.bold ? "text-body-bold-sm content-base-primary" : "text-body-regular-sm content-base-primary",
                      row.italic && "content-base-low",
                    )}>
                    {cell}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
